import React, { useState, useEffect } from 'react';
import { TextField, Button, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const initialState = {
  name: '',
  lastName: '',
  numberId: '',
  phone: '',
  notes: '',
  cargo: '',
  grupoRotacion: ''
};

const OperatorForm = () => {
  const [form, setForm] = useState(initialState);
  const [operators, setOperators] = useState([]);
  const [editId, setEditId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cargos, setCargos] = useState([]);

  // Cargar operarios al montar
  useEffect(() => {
    fetchOperators();
    fetchCargos();
  }, []);

  const fetchOperators = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/operators', { 
        credentials: 'include',
        headers: {
          'x-access-token': token
        }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setOperators(data);
      } else if (data && Array.isArray(data.data)) {
        setOperators(data.data);
      } else {
        setOperators([]);
        alert(data.message || 'No se pudieron cargar los operarios.');
      }
    } catch (err) {
      alert('Error al cargar operarios');
      setOperators([]);
    }
  };

  const fetchCargos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/cargos', {
        credentials: 'include',
        headers: { 'x-access-token': token },
      });
      const data = await res.json();
      if (Array.isArray(data)) setCargos(data);
    } catch {
      setCargos([]);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      if (editId) {
        // Editar operario
        const res = await fetch(`/api/operators/${editId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'x-access-token': token
          },
          body: JSON.stringify(form),
          credentials: 'include'
        });
        if (res.ok) {
          setEditId(null);
          setForm(initialState);
          fetchOperators();
        } else {
          const data = await res.json();
          alert(data.message || 'Error al editar operario');
        }
      } else {
        // Crear operario
        const res = await fetch('/api/operators', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-access-token': token
          },
          body: JSON.stringify(form),
          credentials: 'include'
        });
        if (res.ok) {
          setForm(initialState);
          fetchOperators();
        } else {
          const data = await res.json();
          alert(data.message || 'Error al crear operario');
        }
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  const handleEdit = (operator) => {
    setForm({
      name: operator.name,
      lastName: operator.lastName,
      numberId: operator.numberId,
      phone: operator.phone,
      notes: operator.notes,
      cargo: operator.cargo || '',
      grupoRotacion: operator.grupoRotacion || ''
    });
    setEditId(operator._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [deleteId, setDeleteId] = useState(null);

  const handleDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/operators/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'x-access-token': token
        }
      });
      if (res.ok) {
        setDeleteId(null);
        setOpenDialog(false);
        fetchOperators();
      } else {
        const data = await res.json();
        alert(data.message || 'Error al eliminar operario');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  return (
    <div>
      <Paper sx={{ p: 3, maxWidth: 500, margin: 'auto', mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          {editId ? 'Editar operario' : 'Crear nuevo operario'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nombre"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Apellido"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Número de identificación"
                name="numberId"
                value={form.numberId}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Teléfono"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notas"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Cargo"
                name="cargo"
                value={form.cargo}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="">-- Sin cargo --</MenuItem>
                {cargos.map((c) => (
                  <MenuItem key={c._id} value={c.cargo}>
                    {c.cargo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Grupo de rotación"
                name="grupoRotacion"
                value={form.grupoRotacion}
                onChange={handleChange}
                fullWidth
                helperText="G0/G1/G2 rotan según el ciclo semanal. Fijo permanece siempre en el mismo turno."
              >
                <MenuItem value="">-- Sin asignar --</MenuItem>
                <MenuItem value="G0">G0 — Grupo A (rota)</MenuItem>
                <MenuItem value="G1">G1 — Grupo B (rota)</MenuItem>
                <MenuItem value="G2">G2 — Grupo C (rota)</MenuItem>
                <MenuItem value="Fijo-T1">Fijo T1 (06:00–14:00)</MenuItem>
                <MenuItem value="Fijo-T2">Fijo T2 (14:00–22:00)</MenuItem>
                <MenuItem value="Fijo-T3">Fijo T3 (22:00–06:00)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth>
                {editId ? 'Actualizar operario' : 'Guardar operario'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Tabla de operarios */}
      <TableContainer component={Paper} sx={{ maxWidth: 900, margin: '40px auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Apellido</TableCell>
              <TableCell>Número ID</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Notas</TableCell>
              <TableCell>Editar</TableCell>
              <TableCell>Eliminar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(operators) ? operators : []).map((op) => (
              <TableRow key={op._id}>
                <TableCell>{op.name}</TableCell>
                <TableCell>{op.lastName}</TableCell>
                <TableCell>{op.numberId}</TableCell>
                <TableCell>{op.phone}</TableCell>
                <TableCell>{op.cargo || '—'}</TableCell>
                <TableCell>{op.grupoRotacion || '—'}</TableCell>
                <TableCell>{op.notes}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleEdit(op)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton color="error" onClick={() => { setDeleteId(op._id); setOpenDialog(true); }}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>¿Eliminar operario?</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar este operario?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OperatorForm;